/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  message,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import Table from 'components/Table';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';

import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { checkToken } from 'api/utils';

import { getEstadosGlobales } from 'api/catalogos';

import {
  getTopesSbcImss,
  getTopeSbcImss,
  postTopeSbcImss,
  patchTopeSbcImss,
  deleteTopeSbcImss,
  autorizarTopeSbcImss,
} from 'api/catalogos/TopeSbcImss';

import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';

export const topeSbcImssPermission = {
  permissionModel: 'topesbcimss',
  catalogsModels: [],
  tabsModels: [],
};

const TopeSbcImss = ({ permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);

  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  const [periodo, setPeriodo] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTopesSbcImss();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();

      const [
        _estados,
        _periodo,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getTopesSbcImss(),
      ]);

      if (mounted) {
        setEstados(_estados);
        setPeriodo(_periodo);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
    form.setFieldsValue({
      ...record,
      vigencia_final: record.vigencia_final
        ? dayjs(record.vigencia_final)
        : null,
    });
  };

  const onClickAdd = () => {
    form.resetFields();
    setSelectedRowKeys([]);
    setSelectedData({});
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _registro = await getTopeSbcImss(key);

    if (_registro) {
      form.setFieldsValue({
        ...formatReceived(_registro),
        vigencia_final: _registro.vigencia_final
          ? dayjs(_registro.vigencia_final)
          : null,
      });
      setDisableFields(_registro.estados_globales !== ESTATUS_ACTIVO);
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.vigencia_final = values.vigencia_final ? values.vigencia_final.format('YYYY-MM-DD') : null;

      const saved = key
        ? await patchTopeSbcImss(key, values)
        : await postTopeSbcImss(values);

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit();
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteTopeSbcImss(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const onConfirmAutorizar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await autorizarTopeSbcImss(key);
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const onConfirmCancelar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await patchTopeSbcImss(key, { estados_globales: ESTATUS_CANCELADO });
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const onConfirmReactivar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await patchTopeSbcImss(key, { estados_globales: ESTATUS_ACTIVO });
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 120,
      render: (val) => (val && periodo ? periodo.find((item) => item.id === val)?.periodo : 'N/A'),

    },
    {
      titleText: 'Tope',
      dataIndex: 'tope',
      key: 'tope',
      width: 100,
    },
    {
      titleText: 'Unidad de Tope',
      dataIndex: 'unidad_de_tope',
      key: 'unidad_de_tope',
      width: 150,
    },
    {
      titleText: 'Valor de Unidad Tope',
      dataIndex: 'valor_de_unidad_tope',
      key: 'valor_de_unidad_tope',
      width: 180,
      render: (val) => `$ ${val}`,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 160,
      render: (val) => (val ? val?.format(DATE_FRONTEND_FORMAT) : 'N/A'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 140,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={{ type: 'radio', selectedRowKeys }}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'topesbcimss', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: 'topesbcimss', action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: true,
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_CANCELADO,
              className: 'cancel',
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: 'topesbcimss', action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: true,
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
        />

        <Modal
          open={open}
          closable={false}
          footer={null}
          onCancel={onCancel}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tope SBC IMSS`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="periodo"
                label="Ejercicio"
                rules={[{ required: true, message: 'El ejercicio es requerido' }]}
                hasFeedback
              >
                <Select
                  dataSource={periodo}
                  keyProp="id"
                  labelProp="periodo"
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item name="tope" label="Tope" rules={[{ required: true }]}>
                <Input type="number" disabled={disableFields} />
              </Form.Item>

            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item name="unidad_de_tope" label="Unidad de Tope" rules={[{ required: true }]}>
                <Input disabled={disableFields} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="valor_de_unidad_tope"
                label="Valor de Unidad de Tope"
                rules={[{ required: true }]}
              >
                <Input prefix="$" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item name="vigencia_final" label="Vigencia Final">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item name="estados_globales" label="Estatus">
                <Select
                  dataSource={estados}
                  labelProp="descripcion"
                  keyProp="id"
                  disabled
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>

            </Col>

          </Form>
        </Modal>

        <ModalDelete
          open={visibleAlert}
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          loading={loading}
          content="Tope SBC IMSS"
        />

        <ModalConfirmation
          open={visibleAutorizar}
          onFinish={onConfirmAutorizar}
          onCancel={() => setVisibleAutorizar(false)}
          ConfirmAutorize
          contentTitle="Autorizar Tope SBC IMSS"
          loading={loading}
        />

        <ModalConfirmation
          open={visibleCancelar}
          onFinish={onConfirmCancelar}
          onCancel={() => setVisibleCancelar(false)}
          ConfirmCancel
          contentTitle="Cancelar Tope SBC IMSS"
          loading={loading}
        />

        <ModalConfirmation
          open={visibleReactivar}
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle="Reactivar Tope SBC IMSS"
          loading={loading}
        />

        <ModalConfirmation
          open={visibleConfirmInactivo}
          ConfirmInactive
          onFinish={() => setVisibleConfirmInactivo(false)}
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          contentTitle="Inactivar Tope SBC IMSS"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TopeSbcImss;
